import { hide, Intercom, onHide, onShow, show, shutdown, update } from '@intercom/messenger-js-sdk'
import { buttonClasses } from '@mui/material/Button'
import { svgIconClasses } from '@mui/material/SvgIcon'
import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { create } from 'zustand'
import { combine } from 'zustand/middleware'
import { IconButton } from './components/IconButton.tsx'
import { fetchIntercomToken } from './data/insight.ts'
import { usePreferences } from './data/userProfile.ts'
import { env } from './env.ts'
import { ids } from './ids.ts'
import { useFlags } from './launchdarkly.ts'

const useIntercom = create(combine({ ready: false, open: false }, () => ({})))

export const IntercomMessenger = () => {
	const { intercom } = useFlags()
	const { data: pref } = usePreferences()

	useEffect(() => {
		update({ insight_language: pref.language, language_override: pref.language })
	}, [pref.language])

	useEffect(() => {
		if (!intercom) return
		let booting = true
		fetchIntercomToken()
			.then((token) => {
				if (!booting) return
				// Launcher is our own HelpButton, rendered in the page layouts
				Intercom({ app_id: env.INTERCOM_APP_ID, intercom_user_jwt: token, hide_default_launcher: true, language_override: pref.language })
				onShow(() => useIntercom.setState({ open: true }))
				onHide(() => useIntercom.setState({ open: false }))
				useIntercom.setState({ ready: true })
			})
			.catch((error) => Sentry.captureException(error))
		return () => {
			booting = false
			shutdown()
			useIntercom.setState({ ready: false, open: false })
		}
	}, [intercom, pref.language])

	return null
}

export const HelpButton = ({ size }: { size: 'small' | 'large' }) => {
	const { intercomMessenger } = useFlags()
	const { t } = useTranslation()
	const ready = useIntercom((s) => s.ready)
	const open = useIntercom((s) => s.open)
	if (!intercomMessenger || !ready) return null
	return (
		<IconButton
			data-ta={ids.common.helpBtn}
			{...tourId('help-button')}
			icon='QuestionSolid'
			label={t(($) => $.help)}
			variant='contained'
			color='secondary'
			size={size}
			// Theme fixes startIcon at 16px for all button sizes
			sx={size === 'large' ? { [`& .${buttonClasses.startIcon} .${svgIconClasses.root}`]: { fontSize: 18 } } : undefined}
			onClick={() => (open ? hide() : show())}
		/>
	)
}

export const tourId = (id: (typeof tourIds)[number]) => ({ 'data-intercom-target': id })
const tourIds = [
	'add-button',
	'hamburger-menu',
	'location-list',
	'profile-menu',
	'map-settings-button',
	'map-settings',
	'play-button',
	'help-button',
	'date-range-select',
	'date-range-reset',
	'preset-duration-select',
	'nav-dashboard',
	'dashboard-risk-levels',
	'dashboard-parameter-filter',
	'dashboard-all-risk-analysis',
	'dashboard-favorites-tab',
] as const
